import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useAuthStore } from './auth'
import { usePrinterStore } from './printer'
import { MOCK_MAINTENANCE, mockCrud, loadFromStorage } from '@/services/mockData'
import type { MaintenanceLog, MaintenanceFormData, MaintenanceType, MaintenancePriority } from '@/types'

const STORAGE_KEY = 'maintenance'

export const useMaintenanceStore = defineStore('maintenance', () => {
  const logs = ref<MaintenanceLog[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)
  const _loaded = ref(false)
  const _contextKey = ref<string>('personal')

  const pendingMaintenance = computed(() => logs.value.filter(l => !l.is_completed))
  const completedMaintenance = computed(() => logs.value.filter(l => l.is_completed))

  const overdueMaintenance = computed(() => {
    const printerStore = usePrinterStore()
    return logs.value.filter(l => {
      if (l.is_completed) return false
      // Hours-based: overdue when printer exceeds threshold
      if (l.printer_hours_threshold && l.printer_id) {
        const printer = printerStore.getById(l.printer_id)
        return printer ? (printer.printer_hours ?? 0) >= l.printer_hours_threshold : false
      }
      // Date-based
      if (!l.scheduled_date) return false
      return new Date(l.scheduled_date) < new Date()
    })
  })

  const upcomingMaintenance = computed(() =>
    logs.value
      .filter(l => {
        if (l.is_completed) return false
        if (!l.scheduled_date) return false
        const scheduled = new Date(l.scheduled_date)
        const now = new Date()
        const weekFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000)
        return scheduled >= now && scheduled <= weekFromNow
      })
      .sort((a, b) => new Date(a.scheduled_date!).getTime() - new Date(b.scheduled_date!).getTime())
  )

  const byPriority = computed(() => {
    const grouped: Record<MaintenancePriority, MaintenanceLog[]> = { critical: [], high: [], medium: [], low: [] }
    pendingMaintenance.value.forEach(l => grouped[l.priority].push(l))
    return grouped
  })

  const byPrinter = (printerId: string) => logs.value.filter(l => l.printer_id === printerId)
  const getById = (id: string) => logs.value.find(l => l.id === id)

  const fetchLogs = async (printerId?: string, force = false, { silent = false, teamId }: { silent?: boolean; teamId?: string } = {}) => {
    const authStore = useAuthStore()
    if (!authStore.userId) {
      console.warn('[maintenanceStore] fetchLogs called without userId')
      return
    }

    const newContext = teamId || 'personal'
    if (newContext !== _contextKey.value) {
      _loaded.value = false
      _contextKey.value = newContext
    }

    if (_loaded.value && !force) return

    if (!silent) {
      loading.value = true
      error.value = null
    }

    try {
      if (authStore.isDemoMode) {
        await new Promise(resolve => setTimeout(resolve, 200))
        let data = loadFromStorage(STORAGE_KEY, MOCK_MAINTENANCE)
        if (printerId) data = data.filter(l => l.printer_id === printerId)
        logs.value = data
      } else {
        const { supabase } = await import('@/services/supabase')
        let query = supabase
          .from('maintenance_logs')
          .select('*')
        if (teamId) {
          query = query.eq('team_id', teamId)
        } else {
          query = query.is('team_id', null).eq('user_id', authStore.userId)
        }
        query = query.order('created_at', { ascending: false })

        if (printerId) query = query.eq('printer_id', printerId)
        const { data, error: fetchError } = await query
        if (fetchError) throw fetchError
        logs.value = data as MaintenanceLog[]
      }
      _loaded.value = true
    } catch (err) {
      if (!silent) error.value = err instanceof Error ? err.message : 'Failed to fetch maintenance logs'
    } finally {
      if (!silent) loading.value = false
    }
  }

  const clearData = () => {
    logs.value = []
    _loaded.value = false
  }

  const createLog = async (logData: MaintenanceFormData, teamId?: string) => {
    const authStore = useAuthStore()
    if (!authStore.userId) return { success: false, error: 'Not authenticated' }

    loading.value = true
    try {
      if (authStore.isDemoMode) {
        const newLog = mockCrud.create(STORAGE_KEY, {
          ...logData,
          user_id: authStore.userId,
          is_completed: false,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        } as MaintenanceLog, logs.value)
        logs.value.unshift(newLog)

        if (logData.priority === 'critical') {
          const printerStore = usePrinterStore()
          await printerStore.updateStatus(logData.printer_id, 'maintenance')
        }
        return { success: true, data: newLog }
      } else {
        const { supabase } = await import('@/services/supabase')
        const { data, error: insertError } = await supabase
          .from('maintenance_logs')
          .insert({ ...logData, user_id: authStore.userId, ...(teamId ? { team_id: teamId } : {}), is_completed: false })
          .select()
          .single()

        if (insertError) throw insertError
        logs.value.unshift(data as MaintenanceLog)

        if (logData.priority === 'critical') {
          const printerStore = usePrinterStore()
          await printerStore.updateStatus(logData.printer_id, 'maintenance')
        }
        return { success: true, data }
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to create maintenance log'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  const updateLog = async (id: string, updates: Partial<MaintenanceFormData>) => {
    const authStore = useAuthStore()
    loading.value = true

    try {
      if (authStore.isDemoMode) {
        const updated = mockCrud.update(STORAGE_KEY, id, updates, logs.value)
        if (updated) {
          const index = logs.value.findIndex(l => l.id === id)
          if (index !== -1) logs.value[index] = updated
        }
        return { success: true, data: updated }
      } else {
        const { supabase } = await import('@/services/supabase')
        const { data, error: updateError } = await supabase
          .from('maintenance_logs')
          .update({ ...updates, updated_at: new Date().toISOString() })
          .eq('id', id)
          .select()
          .single()

        if (updateError) throw updateError
        const index = logs.value.findIndex(l => l.id === id)
        if (index !== -1) logs.value[index] = data as MaintenanceLog
        return { success: true, data }
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to update maintenance log'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  const completeLog = async (id: string, notes?: string) => {
    const log = getById(id)
    if (!log) return { success: false, error: 'Log not found' }

    const updates = {
      is_completed: true,
      completed_date: new Date().toISOString(),
      notes: notes || log.notes
    }

    const result = await updateLog(id, updates as any)

    if (result.success) {
      const printerStore = usePrinterStore()
      await printerStore.recordMaintenance(log.printer_id)

      // Auto-create next occurrence for recurring tasks
      if (log.recurrence_interval_days && log.recurrence_interval_days > 0) {
        const nextDate = new Date()
        nextDate.setDate(nextDate.getDate() + log.recurrence_interval_days)

        await createLog({
          printer_id: log.printer_id,
          type: log.type,
          title: log.title,
          description: log.description,
          priority: log.priority,
          scheduled_date: nextDate.toISOString(),
          recurrence_interval_days: log.recurrence_interval_days,
        }, log.team_id ?? undefined)
      }

      // Auto-create next occurrence for hours-based tasks
      if (log.printer_hours_threshold && log.printer_hours_threshold > 0) {
        const printerStore = usePrinterStore()
        const printer = printerStore.getById(log.printer_id)
        const currentHours = printer?.printer_hours ?? 0
        // Calculate the interval from the original threshold to set the next one
        // e.g., if threshold was 500 and printer is at 520, next threshold = 520 + (500 interval)
        // Use the original threshold as the recurring interval
        const nextThreshold = currentHours + log.printer_hours_threshold

        await createLog({
          printer_id: log.printer_id,
          type: log.type,
          title: log.title,
          description: log.description,
          priority: log.priority,
          printer_hours_threshold: Math.round(nextThreshold * 10) / 10,
        }, log.team_id ?? undefined)
      }
    }

    return result
  }

  const deleteLog = async (id: string) => {
    const authStore = useAuthStore()
    loading.value = true

    try {
      if (authStore.isDemoMode) {
        mockCrud.delete(STORAGE_KEY, id, logs.value)
        logs.value = logs.value.filter(l => l.id !== id)
        return { success: true }
      } else {
        const { supabase } = await import('@/services/supabase')
        const { error: deleteError } = await supabase.from('maintenance_logs').delete().eq('id', id)
        if (deleteError) throw deleteError
        logs.value = logs.value.filter(l => l.id !== id)
        return { success: true }
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to delete maintenance log'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  const scheduleRecurring = async (printerId: string, type: MaintenanceType, intervalDays: number, title: string) => {
    const nextDate = new Date()
    nextDate.setDate(nextDate.getDate() + intervalDays)

    return createLog({
      printer_id: printerId,
      type,
      title,
      description: `Recurring maintenance - every ${intervalDays} days`,
      priority: 'medium',
      scheduled_date: nextDate.toISOString(),
      next_due_date: nextDate.toISOString()
    })
  }

  return {
    logs, loading, error, _loaded, _contextKey,
    pendingMaintenance, completedMaintenance, overdueMaintenance, upcomingMaintenance,
    byPriority, byPrinter, getById,
    fetchLogs, createLog, updateLog, completeLog, deleteLog, scheduleRecurring,
    clearData
  }
})
